import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PhoneIcon from '@mui/icons-material/Phone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface Workflow {
  leadStage: string;
  type: string;
}

interface LeadSourceItem {
  name: string;
  hasTracking: boolean;
}

interface CheckResult {
  success: boolean;
  message: string;
  data?: {
    configuredWorkflows?: Workflow[];
    missingWorkflows?: Workflow[];
    level?: 'success' | 'warning' | 'error';
    sources?: LeadSourceItem[];
  };
}

interface CheckResponse {
  status: 'passed' | 'warning' | 'failed';
  message: string;
  details?: {
    resultsByCommunity?: Record<string, CheckResult>;
  };
}

interface CheckCardProps {
  title: string;
  result: CheckResponse | null;
  loading: boolean;
  communityNames: Record<string, string>;
  onAction?: { label: string; handler: () => void } | null;
}

interface E2EResult {
  aptId: string;
  success: boolean;
  message: string;
}

interface CleanupItem {
  success: boolean;
  leadId?: string;
  tourId?: string;
  message: string;
}

interface E2EReportData {
  leadCreation?: {
    successfulCreations: number;
    totalRequests: number;
  };
  tourScheduling?: {
    successfulBookings: number;
    totalRequests: number;
    results?: E2EResult[];
  };
  cleanup?: {
    skipped?: boolean;
    reason?: string;
    leads?: CleanupItem[];
    tours?: CleanupItem[];
  };
  [key: string]: unknown;
}

interface E2EReportProps {
  report: E2EReportData | null;
  communityNames: Record<string, string>;
}

const REQUIRED_WORKFLOWS: Workflow[] = [
  { leadStage: 'Inbound', type: 'AFM' },
  { leadStage: 'Inbound', type: 'FUM' },
  { leadStage: 'Contacted', type: 'FUM' },
  { leadStage: 'Tour Cancelled', type: 'AFM' },
  { leadStage: 'Tour Cancelled', type: 'FUM' },
  { leadStage: 'Tour No Show', type: 'AFM' },
  { leadStage: 'Tour No Show', type: 'FUM' }
];

const getConfiguredWorkflows = (data: CheckResult['data']): Workflow[] => {
  if (!data?.configuredWorkflows && !data?.missingWorkflows) return [];
  if (data?.configuredWorkflows && data.configuredWorkflows.length > 0)
    return data.configuredWorkflows;
  const missing = data?.missingWorkflows || [];
  const missingKeys = new Set(missing.map(w => `${w.leadStage}|${w.type}`));
  return REQUIRED_WORKFLOWS.filter(
    w => !missingKeys.has(`${w.leadStage}|${w.type}`)
  );
};

export const STATUS_ICON: Record<string, React.ReactNode> = {
  true: <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />,
  false: <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />,
  warning: <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 20 }} />
};

const LEVEL_ICON: Record<string, React.ReactNode> = {
  success: STATUS_ICON['true'],
  warning: STATUS_ICON['warning'],
  error: STATUS_ICON['false']
};

export const CheckCard: React.FC<CheckCardProps> = ({
  title,
  result,
  loading,
  communityNames,
  onAction
}) => {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="subtitle1">Running {title}...</Typography>
          </Box>
          <LinearProgress sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    );
  }

  if (!result) return null;

  const status = result.status; // 'passed' | 'warning' | 'failed'
  const details = result.details?.resultsByCommunity || {};

  const statusConfig = {
    passed: {
      icon: STATUS_ICON['true'],
      label: 'Passed',
      color: 'success' as const
    },
    warning: {
      icon: STATUS_ICON['warning'],
      label: 'Warning',
      color: 'warning' as const
    },
    failed: {
      icon: STATUS_ICON['false'],
      label: 'Failed',
      color: 'error' as const
    }
  };

  const config = statusConfig[status] || statusConfig.failed;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {config.icon}
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
            <Chip
              label={config.label}
              size="small"
              color={config.color}
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onAction && (
              <Button
                size="small"
                variant="outlined"
                onClick={onAction.handler}
              >
                {onAction.label}
              </Button>
            )}
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              Details
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {result.message}
        </Typography>
      </CardContent>
      <Collapse in={expanded}>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Community</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(details).map(([id, r]) => {
                const levelIcon = r.data?.level
                  ? LEVEL_ICON[r.data.level]
                  : STATUS_ICON[String(r.success)];
                return (
                  <TableRow key={id}>
                    <TableCell>{communityNames[id] || id}</TableCell>
                    <TableCell>{levelIcon}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.message}</Typography>
                      {/* Lead sources list */}
                      {r.data?.sources && r.data.sources.length > 0 && (
                        <Box
                          sx={{
                            mt: 0.5,
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.25
                          }}
                        >
                          {r.data.sources.map(
                            (src: LeadSourceItem, i: number) => (
                              <Box
                                key={i}
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                              >
                                {src.hasTracking ? (
                                  <>
                                    <PhoneIcon
                                      sx={{
                                        fontSize: 14,
                                        mr: 0.25,
                                        color: 'success.main'
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      component="span"
                                      fontWeight={700}
                                    >
                                      {src.name}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography variant="body2" component="span">
                                    {src.name}
                                  </Typography>
                                )}
                                {i < r.data!.sources!.length - 1 && (
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ mr: 0.5 }}
                                  >
                                    ,
                                  </Typography>
                                )}
                              </Box>
                            )
                          )}
                        </Box>
                      )}
                      {/* Workflow chips */}
                      {r.data && getConfiguredWorkflows(r.data).length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {getConfiguredWorkflows(r.data).map((w, i) => (
                            <Chip
                              key={`c${i}`}
                              label={`${w.leadStage} / ${w.type}`}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                      {r.data?.missingWorkflows &&
                        r.data.missingWorkflows.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {r.data.missingWorkflows.map((w, i) => (
                              <Chip
                                key={`m${i}`}
                                label={`${w.leadStage} / ${w.type}`}
                                size="small"
                                color="warning"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Card>
  );
};

export const E2EReport: React.FC<E2EReportProps> = ({
  report,
  communityNames
}) => {
  if (!report) return null;

  const sections: {
    key: string;
    title: string;
    render: (data: any) => React.ReactNode;
  }[] = [
    {
      key: 'leadCreation',
      title: 'Lead Creation',
      render: data => (
        <Typography variant="body2">
          {data.successfulCreations}/{data.totalRequests} leads created
        </Typography>
      )
    },
    {
      key: 'tourScheduling',
      title: 'Tour Scheduling',
      render: data => {
        const failed = data.results?.filter((r: E2EResult) => !r.success) || [];
        return (
          <Box>
            <Typography variant="body2">
              {data.successfulBookings}/{data.totalRequests} tours scheduled
            </Typography>
            {failed.map((r: E2EResult, i: number) => (
              <Typography
                key={i}
                variant="caption"
                color="error"
                sx={{ display: 'block' }}
              >
                {communityNames[r.aptId] || r.aptId}: {r.message}
              </Typography>
            ))}
          </Box>
        );
      }
    },
    {
      key: 'cleanup',
      title: 'Cleanup',
      render: data => {
        if (data.skipped)
          return (
            <Typography variant="body2" color="text.secondary">
              Skipped: {data.reason}
            </Typography>
          );
        const leadOk =
          data.leads?.filter((l: CleanupItem) => l.success).length || 0;
        const leadFail =
          data.leads?.filter((l: CleanupItem) => !l.success) || [];
        const tourOk =
          data.tours?.filter((t: CleanupItem) => t.success).length || 0;
        const tourFail =
          data.tours?.filter((t: CleanupItem) => !t.success) || [];
        return (
          <Box>
            <Typography variant="body2">
              {leadOk} leads cleaned, {tourOk} tours cleaned
            </Typography>
            {leadFail.map((l: CleanupItem, i: number) => (
              <Typography
                key={`l${i}`}
                variant="caption"
                color="error"
                sx={{ display: 'block' }}
              >
                Lead {l.leadId}: {l.message}
              </Typography>
            ))}
            {tourFail.map((t: CleanupItem, i: number) => (
              <Typography
                key={`t${i}`}
                variant="caption"
                color="error"
                sx={{ display: 'block' }}
              >
                Tour {t.tourId}: {t.message}
              </Typography>
            ))}
          </Box>
        );
      }
    }
  ];

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          End-to-End Results
        </Typography>
        {sections.map(
          ({ key, title, render }) =>
            report[key] && (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  py: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ minWidth: 130 }}
                >
                  {title}:
                </Typography>
                {render(report[key])}
              </Box>
            )
        )}
      </CardContent>
    </Card>
  );
};
